import {getHexDistance} from "../../coords/getHexDistance";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {COMMON_BUILDING_TOKENS} from "../../../constants/tokens";
import {NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {classifyOwnCity} from "../strategy/bonusAxis";

// Волна 33 («сильные города», см. configs/botBonusBehavior): чистые функции
// перемещения бонусного бота. Все они читаются decideMovementStep/campaigns
// только под гейтом bonusProfile.strongCities && ось удара (bonusAxis) —
// обычные боты и legacy-группа A/B этот модуль не вызывают вовсе.
//
// Что здесь и зачем:
//  - множители давления (M2): к прежнему булеву «игрок под фокусом» (×1.6)
//    добавляется ближайший противник оси с собственным весом; при пересечении
//    берется максимум — множитель, а не правило «все на одного»;
//  - гарнизон фронтирного города (M4): небольшой отряд, единственный на
//    кварталах фронтирного города, получает удержание — иначе его уводят
//    градиенты, и живой игрок забирает пустой город вместе с фермами;
//  - угроза городу без гарнизона (M4): отряд на казарме такого города
//    снимается с удержания роста (и с бонуса роста на казарме) и идет на квартал;
//  - порядок захватных целей (M6) и фокус роя (M8): города ближайшего
//    противника — первыми, чтобы армия била соседа, а не расползалась.

/** Множитель давления по ключу карты; 1 — цель не под давлением */
export const getPressureFactor = (factorByKey, key) => factorByKey?.get(key) ?? 1;

/** Записывает множитель по ключу, оставляя больший из уже записанного и нового */
export const registerPressureFactor = (factorByKey, key, factor) => {
  if (factor > 1 && factor > (factorByKey.get(key) ?? 1)) {
    factorByKey.set(key, factor);
  }
};

/**
 * Карты множителей давления по id игрока и по цвету: игроки под фокусом ботов
 * (рейтинговая защелка) получают pressureWeight (1.6), ближайший противник оси
 * бонусного бота — targetEnemyWeight; при совпадении — максимум. Без оси
 * (обычный бот) карты содержат ровно фокус, то есть прежние 1.6/1.
 */
export const buildPressureFactorMaps = ({pressuredPlayers, pressureWeight, axis = null, targetEnemyWeight = 1}) => {
  const byPlayerId = new Map();
  const byColor = new Map();
  pressuredPlayers.forEach((player) => {
    registerPressureFactor(byPlayerId, player.id, pressureWeight);
    registerPressureFactor(byColor, player.color, pressureWeight);
  });
  if (axis) {
    registerPressureFactor(byPlayerId, axis.enemyPlayerId, targetEnemyWeight);
    registerPressureFactor(byColor, axis.enemyColor, targetEnemyWeight);
  }
  return {byPlayerId, byColor};
};

/** Наибольший множитель давления среди смежных отрядов противника (размен) */
export const calcMaxPressureFactor = (factorByPlayerId, enemies) => enemies.reduce(
  (max, enemy) => Math.max(max, getPressureFactor(factorByPlayerId, enemy.ownerId)), 1
);

/**
 * Индекс гарнизонов фронтирных городов: квартал → {cityKey, quarters,
 * barracks, holderIds}. holderIds — свои отряды, стоящие НА кварталах города
 * (правило захвата: занятый владельцем квартал не захватывается). Тыловые
 * города в индекс не входят — их гарнизон правилами волны 33 не удерживается.
 */
export const buildFrontierGarrisonIndex = ({ownCities, axis, units, currentPlayerId}) => {
  const byQuarter = new Map();
  ownCities.forEach((city) => {
    if (classifyOwnCity(city, axis) !== 'frontier') {
      return;
    }
    const quarters = getAllQuartersOfCity(city);
    const quarterSet = new Set(quarters);
    const holderIds = new Set();
    units.forEach((unit) => {
      if (unit.ownerId === currentPlayerId && quarterSet.has(unit.hexKey)) {
        holderIds.add(unit.id);
      }
    });
    const entry = {
      cityKey: city.centralQuarter,
      quarters,
      barracks: city[COMMON_BUILDING_TOKENS.BARRACKS] || [],
      holderIds,
    };
    quarters.forEach((quarterHexKey) => byQuarter.set(quarterHexKey, entry));
  });
  return byQuarter;
};

/**
 * Удержание гарнизона на ячейке: cellKey — квартал фронтирного города, на
 * кварталах которого нет ДРУГИХ своих отрядов, а сам отряд — держатель по
 * правилам: размер ниже доли максимума (крупные идут воевать), без кампании,
 * не форсированная цель и не на удержании роста на казарме (тот дорастает).
 * Возвращает величину надбавки или 0.
 */
export const calcFrontierGarrisonHold = ({
                                           unit,
                                           cellKey,
                                           garrisonByQuarter,
                                           maxSize,
                                           garrisonSizeShare,
                                           hold,
                                           hasCampaign,
                                           isForcedCapture,
                                           isGrowthHeld,
                                         }) => {
  const entry = garrisonByQuarter.get(cellKey);
  if (!entry || hasCampaign || isForcedCapture || isGrowthHeld) {
    return 0;
  }
  if (maxSize <= 0 || unit.size >= Math.ceil(maxSize * garrisonSizeShare)) {
    return 0;
  }
  const otherHoldersCount = entry.holderIds.size - (entry.holderIds.has(unit.id) ? 1 : 0);
  return otherHoldersCount === 0 ? hold : 0;
};

/**
 * Свои отряды на казармах фронтирных городов без гарнизона, к кварталам которых
 * подошел живой (не нейтральный) противник в радиусе threatRadius. Такие
 * отряды снимаются с удержания роста, а в оценке ячейки им не начисляется бонус
 * роста на казарме (isBarracksGrowthReleased): иначе при смежном лагере (×2) или
 * у малого отряда (×4) рост перебивал бы гарнизонное удержание квартала, и
 * город под угрозой оставался бы пустым. Возвращает Set id отрядов.
 * Волна 37 (B1, гейт у вызывающего кода): isCityThreatened(cityKey) — угроза
 * городу вместо радиуса: только угроза ЭТОГО раунда (отряды и фантомный найм
 * ходящих после бота). Без нее захвата в этом раунде нет, а в следующем отряд
 * с казармы встанет на квартал одним шагом — рост снимать незачем. Не передан —
 * прежний радиус threatRadius
 */
export const findThreatenedGarrisonBarracksUnitIds = ({
                                                        garrisonByQuarter,
                                                        units,
                                                        currentPlayerId,
                                                        threatRadius,
                                                        isCityThreatened = null,
                                                      }) => {
  const result = new Set();
  const seenCities = new Set();
  const enemyCells = units
    .filter((unit) => unit.ownerId !== currentPlayerId && unit.ownerId !== NEUTRAL_PLAYER_ID)
    .map((unit) => unit.hexKey);
  if (enemyCells.length === 0 && !isCityThreatened) {
    return result;
  }
  garrisonByQuarter.forEach((entry) => {
    if (seenCities.has(entry.cityKey)) {
      return;
    }
    seenCities.add(entry.cityKey);
    if (entry.holderIds.size > 0 || entry.barracks.length === 0) {
      return;
    }
    const isThreatened = isCityThreatened
      ? isCityThreatened(entry.cityKey)
      : entry.quarters.some((quarterHexKey) =>
        enemyCells.some((enemyCell) => getHexDistance(enemyCell, quarterHexKey) <= threatRadius));
    if (!isThreatened) {
      return;
    }
    const barracksSet = new Set(entry.barracks);
    units.forEach((unit) => {
      if (unit.ownerId === currentPlayerId && barracksSet.has(unit.hexKey)) {
        result.add(unit.id);
      }
    });
  });
  return result;
};

/**
 * Отряд снят с роста на казарме из-за угрозы фронтирному городу (M4): бонус
 * STAY_ON_BARRACKS_TO_GROW ему не начисляется — квартал города с гарнизонным
 * удержанием должен выиграть у казармы. У обычных ботов набора нет — false
 */
export const isBarracksGrowthReleased = (threatReleasedUnitIds, unitId) => (
  Boolean(threatReleasedUnitIds?.has(unitId))
);

/**
 * Источники тяги простаивающей армии у бонусного бота: кварталы и казармы
 * кузницы (а не один ближайший к врагу квартал) — отряды кластеризуются у
 * города, где стоят казармы, лагерь и арсенал
 */
export const collectStagingPullSources = (stagingCity) => (
  stagingCity
    ? [...getAllQuartersOfCity(stagingCity), ...(stagingCity[COMMON_BUILDING_TOKENS.BARRACKS] || [])]
    : []
);

/**
 * Стабильная перестановка целей: сначала кварталы городов enemyColor (в
 * прежнем относительном порядке), затем остальные. colorByTarget — квартал →
 * цвет владельца; цели без цвета (руины, точки) — в хвост.
 */
export const orderTargetsByEnemyColorFirst = (targets, colorByTarget, enemyColor) => {
  const first = [];
  const rest = [];
  for (const targetHexKey of targets) {
    if (colorByTarget.get(targetHexKey) === enemyColor) {
      first.push(targetHexKey);
    } else {
      rest.push(targetHexKey);
    }
  }
  return [...first, ...rest];
};

/**
 * Фокус роя (крысолюды/инсектоиды) в армейской фазе: взятый город ближайшего
 * противника предпочитается нейтралу, если он не дальше нейтрала больше чем на
 * extraDistance ячеек; без кандидата-нейтрала — он же. Оба фокуса — результаты
 * pickNearestCity ({distance, city, color} | null)
 */
export const pickSwarmFocus = ({neutralFocus, enemyColorFocus, extraDistance}) => {
  if (!enemyColorFocus) {
    return neutralFocus;
  }
  if (!neutralFocus) {
    return enemyColorFocus;
  }
  return enemyColorFocus.distance <= neutralFocus.distance + extraDistance
    ? enemyColorFocus
    : neutralFocus;
};
